// Movimientos (traslados, compras, ventas, bajas). Ganadería → Movimientos.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Animal, AnimalFicha, Campo, RegistrarMovimientoModal, fieldCls, ganRpc, invalidateGanaderia,
  num, sexoLabel, tipoMovLabel,
} from './shared';

export function Movimientos() {
  const qc = useQueryClient();
  const [animalId, setAnimalId] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);

  const { data: animales = [] } = useQuery<Animal[]>({
    queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar'),
  });
  const animal = animales.find((a) => a.id === animalId) ?? null;

  const { data: ficha, isFetching } = useQuery<AnimalFicha>({
    queryKey: ['gan-ficha', animalId],
    queryFn: () => ganRpc<AnimalFicha>('gan_animal_ficha', { _id: animalId }),
    enabled: !!animalId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Truck className="w-5 h-5" /> Movimientos</h1>

      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Animal" className="w-80">
            <select className={fieldCls} value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
              <option value="">— Selecciona un animal —</option>
              {animales.map((a) => (
                <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''} ({sexoLabel(a.sexo)})</option>
              ))}
            </select>
          </Campo>
          <Button disabled={!animal} onClick={() => setModalAbierto(true)}>Registrar movimiento</Button>
        </CardContent>
      </Card>

      {animal && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Movimientos de {animal.arete}{animal.nombre ? ` — ${animal.nombre}` : ''}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Origen</th><th className="py-2 pr-3">Destino</th>
                <th className="py-2 pr-3 text-right">Peso (kg)</th><th className="py-2 pr-3 text-right">Precio</th><th className="py-2 pr-3">Motivo</th>
              </tr></thead>
              <tbody>
                {(!ficha || ficha.movimientos.length === 0) && (
                  <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin movimientos registrados</td></tr>
                )}
                {ficha?.movimientos.map((m, i) => (
                  <tr key={`${m.fecha}-${i}`} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{m.fecha}</td>
                    <td className="py-2 pr-3">{tipoMovLabel(m.tipo)}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{m.potrero_origen ?? '—'}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{m.potrero_destino ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{m.peso_kg != null ? num(m.peso_kg, 1) : '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{m.precio != null ? num(m.precio, 2) : '—'}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{m.motivo ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {!animal && (
        <p className="text-sm text-muted-foreground">Selecciona un animal para registrar un traslado, compra, venta u otra baja, o para ver su historial de movimientos.</p>
      )}

      {modalAbierto && animal && (
        <RegistrarMovimientoModal
          animal={animal}
          onClose={() => setModalAbierto(false)}
          onSaved={() => invalidateGanaderia(qc)}
        />
      )}
    </div>
  );
}
